import { Pin, PinOff, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useTeamChatPins, useTogglePin } from '../../hooks/useTeamChat';
import { Avatar } from '../tasks/linear/Avatar';

/**
 * Body of the pinned-messages panel. Anchoring and dismissal belong to the
 * shared `Popover` that wraps it.
 *
 * This used to own both, with a bespoke document-level `click` listener that
 * only excluded the panel — not the header button that opened it. So the very
 * click that set `open` could reach the listener and close the panel again in
 * the same tick, which is why pinning appeared to do nothing beyond the badge.
 * `Popover` excludes its own anchor, and portals to `document.body` so the
 * panel can't be clipped by the chat column's `overflow-hidden` either.
 */
export function PinnedPanel({
  conversationId,
  onJumpTo,
  onClose,
}: {
  conversationId: string;
  onJumpTo: (messageId: string) => void;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const { data: pins } = useTeamChatPins(conversationId, true);
  const togglePin = useTogglePin();
  const messages = pins ?? [];

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Pin size={14} className="text-amber-500" />
          {t('teamChat.pinned')}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          className="touch-target-auto rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      <div className="max-h-80 overflow-y-auto p-1">
        {messages.map((message) => (
          <div
            key={message.id}
            className="group flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
          >
            <button
              type="button"
              onClick={() => {
                onJumpTo(message.id);
                onClose();
              }}
              className="touch-target-auto flex min-w-0 flex-1 items-start gap-2 text-left"
            >
              <Avatar name={message.author.name || null} size={24} />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-1.5">
                  <span className="truncate text-xs font-semibold text-foreground">
                    {message.author.name}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {new Date(message.createdAt).toLocaleDateString(locale)}
                  </span>
                </span>
                <span className="line-clamp-2 block text-xs text-muted-foreground">
                  {message.body || message.attachments[0]?.fileName || ''}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => togglePin.mutate({ id: message.id, pinned: true })}
              aria-label={t('teamChat.unpin')}
              title={t('teamChat.unpin')}
              className="touch-target-auto shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground focus:opacity-100 group-hover:opacity-100 max-md:opacity-100"
            >
              <PinOff size={14} />
            </button>
          </div>
        ))}
        {messages.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-muted-foreground">
            {t('teamChat.noPins')}
          </p>
        )}
      </div>
    </div>
  );
}
